import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { sleep } from '../lib/utils';
import { SupabaseClient } from '@supabase/supabase-js';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

interface BusinessHours {
  enabled: boolean;
  open: string;
  close: string;
  closed: boolean;
}

interface WeeklySchedule {
  monday: BusinessHours;
  tuesday: BusinessHours;
  wednesday: BusinessHours;
  thursday: BusinessHours;
  friday: BusinessHours;
  saturday: BusinessHours;
  sunday: BusinessHours;
}

interface BookingSettings {
  allow_online_bookings: boolean;
  require_deposit: boolean;
  send_confirmations: boolean;
  buffer_time: number;
  max_bookings_per_day: number | null;
  cancellation_policy: string;
  deposit_amount: number;
  deposit_type: 'percentage' | 'fixed';
  navbar_color?: string;
  logo_url?: string;
}

interface NotificationSettings {
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  reminder_time: string;
  send_booking_confirmations: boolean;
  send_booking_reminders: boolean;
  send_booking_followups: boolean;
}

export interface Business {
  id: string;
  name: string;
  type: string;
  email: string;
  phone: string;
  business_domain_email: string;
  primary_email: string;
  email_verified: boolean;
  address: string;
  timezone: string;
  currency: string;
  business_hours: WeeklySchedule;
  booking_settings: BookingSettings;
  notification_settings: NotificationSettings;
  created_at: string;
  updated_at: string;
  owner_id: string;
  gst_number?: string;
  pst_number?: string;
  slug: string;
}

interface BusinessContextType {
  selectedBusiness: Business | null;
  businessId: string | null;
  setSelectedBusiness: (business: Business | null) => void;
  businesses: Business[];
  role: string;
  loading: boolean;
  error: string | null;
  updateBusiness: (id: string, updates: Partial<Business>) => Promise<void>;
}

export const BusinessContext = createContext<BusinessContextType | null>(null);

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}

interface BusinessProviderProps {
  children: ReactNode;
  business: Business | null;
}

export function BusinessProvider({ children, business }: BusinessProviderProps) {
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(business);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('admin');
  const [error, setError] = useState<string | null>(null);
  const [businessCount, setBusinessCount] = useState(0);
  const [businessId, setBusinessId] = useState<string | null>(business?.id || null);

  useEffect(() => {
    setBusinessId(selectedBusiness?.id || null);
  }, [selectedBusiness]);

  const loadBusinesses = async (retryCount = 0) => {
    if (!retryCount) {
      setLoading(true);
      setError(null);
    }

    if (!supabase) {
      console.error('Supabase client not initialized');
      setError('Database connection not available');
      setLoading(false);
      return;
    }

    try {
      // First check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Auth session check:', { 
        hasSession: !!session, 
        userId: session?.user?.id,
        userMetadata: session?.user?.user_metadata,
        error: sessionError
      });
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw sessionError;
      }

      if (!session?.user) {
        console.log('No user session found, clearing business state');
        setBusinesses([]);
        setSelectedBusiness(null);
        setLoading(false);
        return;
      }

      // Get business ID from user metadata
      const businessId =
        session.user.user_metadata.employed_business_ids ||
        session.user.user_metadata.owned_business_ids;
      
      console.log('Business ID check:', {
        employedBusinessIds: session.user.user_metadata.employed_business_ids,
        ownedBusinessIds: session.user.user_metadata.owned_business_ids,
        finalBusinessId: businessId
      });

      const userIsAdmin = session.user.user_metadata.owned_business_ids;
      if (userIsAdmin) {
        console.log('User is admin');
        setRole('admin');
      } else {
        console.log('Checking resource role');
        const { data: resourceRole, error: roleError } = await supabase
          .from('resources')
          .select('role, auth_user_id')
          .eq('auth_user_id', session.user.id)
          .single();

        console.log('Resource role check:', { resourceRole, error: roleError });

        if (!roleError && resourceRole) {
          setRole(resourceRole.role);
        } else {
          console.error('Error getting resource role:', roleError);
        }
      }

      // Get business data with retry
      let businessData;
      let businessError;
      for (let i = 0; i < 3; i++) {
        console.log(`Attempting to load business data (attempt ${i + 1}/3)`);
        const result = await supabase
          .from('businesses')
          .select('*')
          .eq('id', businessId)
          .order('created_at', { ascending: false });

        if (!result.error) {
          businessData = result.data;
          console.log('Business data loaded:', businessData);
          break;
        }
        businessError = result.error;
        console.error(`Business load attempt ${i + 1} failed:`, businessError);
        await sleep(RETRY_DELAY * Math.pow(2, i));
      }

      if (businessError) throw businessError;

      setBusinesses(businessData || []);
      setBusinessCount((businessData || []).length);
      if (businessData && businessData.length > 0 && !selectedBusiness) {
        console.log('Setting selected business:', businessData[0]);
        setSelectedBusiness(businessData[0]);
      }

      setError(null);
    } catch (err) {
      console.error('Error in loadBusinesses:', err);

      // Retry on network errors
      if (
        retryCount < MAX_RETRIES && 
        (err instanceof Error && err.message.includes('Failed to fetch'))
      ) {
        console.log(`Retrying business load (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await sleep(RETRY_DELAY * Math.pow(2, retryCount));
        return loadBusinesses(retryCount + 1);
      }

      setError(err instanceof Error ? err.message : 'Failed to load businesses');
      setBusinesses([]);
      setSelectedBusiness(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const client = supabase;

    if (!client) {
      console.error('Supabase client not initialized');
      return;
    }

    const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
      console.log('auth state changes', session?.user);
      if (session?.user) {
        loadBusinesses(0);
      } else {
        if (mounted) {
          setBusinesses([]);
          setSelectedBusiness(null);
          setLoading(false);
        }
      }
    });

    loadBusinesses();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const updateBusiness = async (id: string, updates: Partial<Business>) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      const { data, error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setBusinesses((prev) => prev.map((b) => (b.id === id ? data : b)));
      if (selectedBusiness?.id === id) {
        setSelectedBusiness(data);
      }
    } catch (err) {
      console.error('Error updating business:', err);
      throw err;
    }
  };

  useEffect(() => {
    console.log('Business context state updated:', {
      selectedBusiness,
      businessCount,
      loading,
      error,
    });
  }, [selectedBusiness, businesses, loading, error]);

  const value = {
    selectedBusiness,
    businessId,
    setSelectedBusiness,
    businesses,
    role,
    loading,
    error,
    updateBusiness,
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

// Export as a single default object
const BusinessModule = {
  Provider: BusinessProvider,
  useBusiness
};

export default BusinessModule;
